/*************************************************************
 * Enhanced AlertService.js - Alert processing with backend integration
 * Now works with both frontend logic and backend alert system
 *************************************************************/

import { api } from '../api/reportingApi';

/**
 * Utility function to check if current time is within working hours
 */
function isWithinWorkingHours(preferences) {
    const now = new Date();
    const currentTime = now.toTimeString().slice(0, 5); // "HH:MM"
    
    const workingDays = preferences.workingDays || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    const startTime = preferences.workingHoursStart || '08:00';
    const endTime = preferences.workingHoursEnd || '18:00';
    
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const todayName = dayNames[now.getDay()];
    
    const isWorkingDay = workingDays.includes(todayName);
    const isWorkingTime = currentTime >= startTime && currentTime <= endTime;
    
    return isWorkingDay && isWorkingTime;
}

export class AlertService {
    constructor() {
        this.lastAlertTimes = {};
        this.alertPollingInterval = null;
        this.alertCache = [];
        this.isPollingActive = false;
    }

    /**
     * Start polling for backend alerts (for OverviewTab integration)
     */
    startBackendAlertPolling(callback, interval = 30000) {
        if (this.isPollingActive) {
            console.warn('[AlertService] Polling already active');
            return this.alertPollingInterval;
        }

        console.log(`[AlertService] Starting backend alert polling every ${interval}ms`);
        this.isPollingActive = true;

        const pollAlerts = async () => {
            try {
                // Use the backend alert endpoints
                const [alertsResponse, summaryResponse, notificationsResponse] = await Promise.allSettled([
                    api.getAlerts(),
                    api.getAlertsSummary(), 
                    api.getInAppNotifications(20)
                ]);

                let alerts = [];
                let summary = {};
                let notifications = [];

                if (alertsResponse.status === 'fulfilled' && alertsResponse.value.success) {
                    alerts = alertsResponse.value.data || [];
                    this.alertCache = alerts; // Cache for other methods
                }

                if (summaryResponse.status === 'fulfilled' && summaryResponse.value.success) {
                    summary = summaryResponse.value.data || {};
                }

                if (notificationsResponse.status === 'fulfilled' && notificationsResponse.value.success) {
                    notifications = notificationsResponse.value.data || [];
                }

                if (callback && typeof callback === 'function') {
                    callback({
                        alerts,
                        summary,
                        notifications,
                        timestamp: new Date().toISOString()
                    });
                }

                console.log(`[AlertService] Polled ${alerts.length} alerts, ${notifications.length} notifications`);

            } catch (error) {
                console.error('[AlertService] Error during backend alert polling:', error);
            }
        };

        // Poll immediately, then on interval
        pollAlerts();
        this.alertPollingInterval = setInterval(pollAlerts, interval);
        
        return this.alertPollingInterval;
    }

    /**
     * Stop backend alert polling
     */
    stopBackendAlertPolling() {
        if (this.alertPollingInterval) {
            clearInterval(this.alertPollingInterval);
            this.alertPollingInterval = null;
            this.isPollingActive = false;
            console.log('[AlertService] Stopped backend alert polling');
        }
    }

    /**
     * Get cached alerts from last poll
     */
    getCachedAlerts() {
        return this.alertCache;
    }

    /**
     * Acknowledge an alert via backend
     */
    async acknowledgeAlert(alertId) {
        try {
            console.log(`[AlertService] Acknowledging alert: ${alertId}`);
            const response = await api.acknowledgeAlert(alertId);
            
            if (response.success) {
                // Update cache
                this.alertCache = this.alertCache.map(alert => 
                    alert.alert_id === alertId 
                        ? { ...alert, status: 'acknowledged' }
                        : alert
                );
                
                console.log(`[AlertService] Alert ${alertId} acknowledged successfully`);
                return true;
            }
            return false;
        } catch (error) {
            console.error(`[AlertService] Error acknowledging alert ${alertId}:`, error);
            return false;
        }
    }

    /**
     * Resolve an alert via backend
     */
    async resolveAlert(alertId) {
        try {
            console.log(`[AlertService] Resolving alert: ${alertId}`);
            const response = await api.resolveAlert(alertId);
            
            if (response.success) {
                // Update cache
                this.alertCache = this.alertCache.filter(alert => alert.alert_id !== alertId);
                
                console.log(`[AlertService] Alert ${alertId} resolved successfully`);
                return true;
            }
            return false;
        } catch (error) {
            console.error(`[AlertService] Error resolving alert ${alertId}:`, error);
            return false;
        }
    }

    /**
     * Format alert for display in UI components
     */
    formatAlertForUI(alert) {
        if (!alert) return null;

        // Convert backend alert format to UI-friendly format
        return {
            id: alert.alert_id,
            name: alert.rule_name,
            type: alert.rule_type,
            severity: alert.severity,
            status: alert.status,
            message: this.formatAlertMessage(alert),
            timeAgo: this.formatTimeAgo(alert.triggered_at),
            triggerValue: alert.trigger_value,
            threshold: alert.threshold,
            stationId: alert.station_id,
            projectName: alert.project_name,
            triggeredAt: alert.triggered_at,
            canAcknowledge: alert.status === 'active',
            canResolve: alert.status === 'active' || alert.status === 'acknowledged'
        };
    }

    /**
     * Format backend alert message for display
     */
    formatAlertMessage(alert) {
        const station = alert.station_id ? ` on ${alert.station_id}` : '';
        
        switch (alert.rule_type) {
            case 'defect_rate':
                return `Defect rate ${alert.trigger_value}% exceeded threshold of ${alert.threshold}%${station}`;
            case 'low_confidence':
                return `Model confidence ${alert.trigger_value}% below threshold of ${alert.threshold}%${station}`;
            case 'station_offline':
                return `Station${station} is offline`;
            case 'consecutive_defects':
                return `${alert.trigger_value} consecutive defects detected${station}`;
            case 's500_malfunction':
                const malfunctions = Array.isArray(alert.trigger_value) 
                    ? alert.trigger_value.join(', ') 
                    : alert.trigger_value;
                return `S500 system malfunction detected${station}: ${malfunctions}`;
            case 'defect_type_spike':
                return `Defect type spike detected${station}: ${alert.trigger_value}`;
            case 'general_defect_spike':
                return `General defect spike detected${station}: ${alert.trigger_value}`;
            case 'confidence_drop':
                return `Confidence drop detected${station}: ${alert.trigger_value}`;
            default:
                return alert.rule_name;
        }
    }

    /**
     * Format timestamp as time ago
     */
    formatTimeAgo(timestamp) {
        if (!timestamp) return 'Unknown';
        
        const now = new Date();
        const alertTime = new Date(timestamp);
        const diffMs = now - alertTime;
        const diffMins = Math.floor(diffMs / 60000);
        
        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        
        const diffHours = Math.floor(diffMins / 60);
        if (diffHours < 24) return `${diffHours}h ago`;
        
        const diffDays = Math.floor(diffHours / 24);
        return `${diffDays}d ago`;
    }

    /**
     * Get CSS class for alert severity
     */
    getSeverityClass(severity) {
        const classMap = {
            'low': 'alert-info',
            'medium': 'alert-warning', 
            'high': 'alert-danger',
            'critical': 'alert-dark'
        };
        return classMap[severity] || 'alert-secondary';
    }

    /**
     * Get badge class for alert severity
     */
    getSeverityBadgeClass(severity) {
        const classMap = {
            'low': 'badge-info',
            'medium': 'badge-warning',
            'high': 'badge-danger', 
            'critical': 'badge-dark'
        };
        return classMap[severity] || 'badge-secondary';
    }

    /**
     * LEGACY: Check if any alert rules should be triggered based on current data
     * This is maintained for backward compatibility with frontend-only alert logic
     */
    static checkAlertConditions(data, alertRules) {
        if (!Array.isArray(alertRules)) {
            console.warn('[AlertService] No alert rules provided');
            return [];
        }
        
        const triggeredAlerts = [];
        
        alertRules
            .filter(rule => rule.enabled)
            .forEach(rule => {
                const shouldTrigger = this.evaluateAlertRule(data, rule);
                if (shouldTrigger) {
                    triggeredAlerts.push({
                        ...rule,
                        triggeredAt: new Date().toISOString(),
                        triggerValue: this.extractTriggerValue(data, rule)
                    });
                }
            });
        
        if (triggeredAlerts.length > 0) {
            console.log(`[AlertService] ${triggeredAlerts.length} frontend alerts triggered:`, 
                triggeredAlerts.map(a => a.name));
        }
        
        return triggeredAlerts;
    }
    
    /**
     * LEGACY: Evaluate a single alert rule against data (frontend logic)
     */
    static evaluateAlertRule(data, rule) {
        const { type, threshold, conditions } = rule;
        
        switch (type) {
            case 'defect_rate':
                const defectRate = data.defectRate || data.defect_rate || 0;
                return defectRate > threshold;
                
            case 'low_confidence':
                const confidence = data.confidence || data.avg_confidence || data.model_confidence || 0;
                const confidencePercent = confidence > 1 ? confidence : confidence * 100;
                return confidencePercent < threshold;
                
            case 'station_offline':
                return data.stationStatus === 'offline' || 
                       data.station_status === 'offline' ||
                       data.status === 'offline';
                
            case 'consecutive_defects':
                // This requires stateful tracking, better handled by backend
                console.warn('[AlertService] consecutive_defects should be handled by backend');
                return false;
                
            case 's500_malfunction':
                return data.system_errors?.length > 0 || data.error_types?.length > 0;
                
            case 'defect_type_spike':
                // This requires historical analysis, better handled by backend
                console.warn('[AlertService] defect_type_spike should be handled by backend');
                return false;
                
            case 'session_duration':
                const duration = data.sessionDuration || data.session_duration || 0;
                const durationMinutes = duration > 3600 ? duration / 60 : duration;
                return durationMinutes > threshold;
                
            default:
                console.warn(`[AlertService] Unknown alert rule type: ${type}`);
                return false;
        }
    }
    
    /**
     * LEGACY: Extract the actual value that triggered the alert
     */
    static extractTriggerValue(data, rule) {
        switch (rule.type) {
            case 'defect_rate':
                return data.defectRate || data.defect_rate || 0;
            case 'low_confidence':
                const confidence = data.confidence || data.avg_confidence || data.model_confidence || 0;
                return confidence > 1 ? confidence : confidence * 100;
            case 'station_offline':
                return data.stationStatus || data.station_status || data.status;
            case 'session_duration':
                return data.sessionDuration || data.session_duration || 0;
            case 's500_malfunction':
                return data.system_errors || data.error_types || [];
            default:
                return 'N/A';
        }
    }

    /**
     * LEGACY: Send alert notifications through configured channels
     * NOTE: For production, use backend alert processing instead
     */
    static async sendAlert(alertRule, data, notificationSettings, userPreferences) {
        console.log(`[AlertService] Processing frontend alert: ${alertRule.name}`);
        console.warn('[AlertService] Frontend alert processing is deprecated. Use backend alert system instead.');
        
        // Check working hours for certain alert types
        if (this.shouldRespectWorkingHours(alertRule.type) && 
            !isWithinWorkingHours(userPreferences)) {
            console.log(`[AlertService] Skipping ${alertRule.type} alert outside working hours`);
            return;
        }
        
        const alertData = {
            rule: alertRule,
            data: data,
            timestamp: new Date().toISOString(),
            triggerValue: alertRule.triggerValue || this.extractTriggerValue(data, alertRule)
        };
        
        // Only send in-app notification for frontend alerts
        if (notificationSettings.app_enabled !== false) {
            this.sendInAppNotification(alertData);
        }
        
        console.log('[AlertService] For full notification support, ensure alerts are processed by backend');
    }

    /**
     * Send in-app notification (works with both frontend and backend alerts)
     */
    static sendInAppNotification(alertData) {
        const notification = {
            type: this.getNotificationTypeForAlert(alertData.rule?.type || alertData.rule_type),
            title: `Alert: ${alertData.rule?.name || alertData.rule_name}`,
            message: typeof alertData === 'string' ? alertData : this.formatAlertMessage(alertData, false),
            duration: 10000,
            priority: 'high'
        };
        
        // Try multiple ways to send in-app notifications
        if (window.addNotification) {
            window.addNotification(notification);
        } else if (window.showNotification) {
            window.showNotification(notification);
        } else {
            console.log('[AlertService] In-app notification:', notification);
        }
    }

    /**
     * Get notification type for alert
     */
    static getNotificationTypeForAlert(alertType) {
        switch (alertType) {
            case 'defect_rate':
            case 'defect_type_spike':
            case 'general_defect_spike':
                return 'danger';
            case 'station_offline':
            case 's500_malfunction':
                return 'warning';
            case 'low_confidence':
            case 'confidence_drop':
                return 'warning';
            case 'consecutive_defects':
                return 'danger';
            case 'session_duration':
                return 'info';
            default:
                return 'warning';
        }
    }

    /**
     * Determine if alert type should respect working hours
     */
    static shouldRespectWorkingHours(alertType) {
        // Station offline and S500 malfunction alerts typically only matter during working hours
        return ['station_offline', 's500_malfunction'].includes(alertType);
    }

    /**
     * Check if alert should be throttled (prevent spam)
     */
    shouldThrottleAlert(alertRule, lastAlertTimes = {}) {
        const now = Date.now();
        const ruleKey = `${alertRule.type}_${alertRule.id}`;
        const lastAlertTime = lastAlertTimes[ruleKey] || 0;
        
        // Throttle rules (minimum time between same alerts)
        const throttleMinutes = {
            'defect_rate': 15,
            'station_offline': 5,
            'low_confidence': 30,
            'consecutive_defects': 10,
            's500_malfunction': 2,
            'defect_type_spike': 20,
            'general_defect_spike': 15,
            'confidence_drop': 25,
            'session_duration': 60
        };
        
        const throttleMs = (throttleMinutes[alertRule.type] || 15) * 60 * 1000;
        const timeSinceLastAlert = now - lastAlertTime;
        
        if (timeSinceLastAlert < throttleMs) {
            console.log(`[AlertService] Throttling alert ${alertRule.name} (${Math.round(timeSinceLastAlert/1000/60)} min since last)`);
            return true;
        }
        
        return false;
    }

    /**
     * Test notification system
     */
    async testNotifications(notificationSettings) {
        try {
            console.log('[AlertService] Testing notification system...');
            
            // Test backend notification system if available
            if (api.testNotificationSettings) {
                const result = await api.testNotificationSettings();
                console.log('[AlertService] Backend notification test result:', result);
                return result;
            }

            // Fallback to frontend test
            const testAlert = {
                rule: {
                    name: 'Test Alert - Enhanced System',
                    type: 'defect_rate'
                },
                triggerValue: '7.5%',
                timestamp: new Date().toISOString()
            };

            this.sendInAppNotification(testAlert);
            return { success: true, method: 'frontend_only' };

        } catch (error) {
            console.error('[AlertService] Error testing notifications:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Initialize alert service for OverviewTab
     */
    static createForOverview() {
        return new AlertService();
    }
}

// Export utility functions for use elsewhere
export { isWithinWorkingHours };